// Builds the standalone, print-ready HTML document that ReportsPage renders in
// an iframe and hands to the download button.
//
// Two content shapes arrive here:
//   * full HTML  -- a complete `<!DOCTYPE html>` document (what generate-report
//                  emits once the HTML prompt is deployed). Used as-is.
//   * markdown   -- every report row written before that (content is markdown,
//                  see supabase_schema.sql: "content text not null -- markdown
//                  from Claude"). Converted to semantic HTML and wrapped in the
//                  same shell, so old reports pick up the new design too rather
//                  than staying plain until they're regenerated.
//
// The design language mirrors NG-IT-Weekly-Report_2026-W35_Aug24-29_v2.html:
// grey ground, Archivo/IBM Plex, hairline rules, mono micro-labels, numbered
// section heads, banded tables and uppercase status pills.

/** Detects a complete HTML document rather than a fragment or markdown. */
export function isFullHtmlDoc(content) {
  if (!content) return false;
  return /^\s*(<!doctype\s+html|<html[\s>])/i.test(content);
}

/**
 * Detects HTML markup of any shape (full document, or a bare body fragment the
 * model emitted without the doctype wrapper).
 *
 * Anchored to the start of the content on purpose: a markdown report can carry
 * a literal `<div>` inside a code span or table cell, and an unanchored "does
 * it contain a block tag anywhere" test misreads that whole report as HTML and
 * renders the markdown source raw. Markdown reports open with a heading or
 * prose, never with a structural tag.
 */
export function isHtmlContent(content) {
  if (!content) return false;
  if (isFullHtmlDoc(content)) return true;
  return /^\s*<(section|div|header|main|article|table|h[1-6])[\s>]/i.test(content);
}

const ESC = { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" };
const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ESC[c]);

/** Inline markdown: bold, italic, code, links. Escaped first. */
function inlineMd(raw) {
  return esc(raw)
    .replace(/`([^`]+)`/g, "<code>$1</code>")
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/(^|[\s(])\*([^*\n]+)\*/g, "$1<em>$2</em>")
    .replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2">$1</a>');
}

const isTableRow = (l) => /^\s*\|.*\|\s*$/.test(l);
const isTableDivider = (l) => /^\s*\|[\s:|-]+\|\s*$/.test(l);
const splitRow = (l) => l.trim().replace(/^\||\|$/g, "").split("|").map((c) => c.trim());

// Status words inside a table cell become pills, matching the sample's stage
// vocabulary. Anything else is rendered as plain inline markdown.
const PILL = {
  open: "p-open", wip: "p-wip", "in progress": "p-wip", test: "p-test", testing: "p-test",
  uat: "p-uat", cutover: "p-cut", live: "p-live", done: "p-live", completed: "p-live",
  hold: "p-hold", blocked: "p-hold", high: "p-hi", medium: "p-med", med: "p-med", low: "p-low",
};

function cellHtml(cell) {
  const key = cell.trim().toLowerCase();
  if (PILL[key]) return `<span class="pill ${PILL[key]}">${esc(cell.trim())}</span>`;
  return inlineMd(cell);
}

/**
 * Markdown -> HTML for the report shell. Supports headings, bullet and ordered
 * lists, GFM pipe tables, blockquotes, rules and paragraphs -- the full set the
 * generate-report markdown prompt can produce.
 */
export function markdownToHtml(md) {
  if (!md) return "";
  const lines = String(md).replace(/\r\n/g, "\n").split("\n");
  const out = [];
  let list = null; // "ul" | "ol"
  // Markdown wraps prose across source lines: consecutive non-blank lines are
  // ONE paragraph, and consecutive "> " lines are ONE blockquote. Emitting a
  // <p> per source line (as the original renderer did) breaks sentences
  // mid-clause and turns a two-line quote into two stacked boxes.
  let para = [];
  let quote = [];

  const flushPara = () => {
    if (para.length) { out.push(`<p>${para.join(" ")}</p>`); para = []; }
  };
  const flushQuote = () => {
    if (quote.length) { out.push(`<blockquote><p>${quote.join(" ")}</p></blockquote>`); quote = []; }
  };
  const closeList = () => { if (list) { out.push(`</${list}>`); list = null; } };
  const openList = (kind) => {
    if (list && list !== kind) closeList();
    if (!list) { out.push(`<${kind}>`); list = kind; }
  };
  // Any non-prose construct ends whichever run was open.
  const flushAll = () => { flushPara(); flushQuote(); closeList(); };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Pipe table: header row + divider + body rows.
    if (isTableRow(line) && isTableDivider(lines[i + 1] ?? "")) {
      flushAll();
      const head = splitRow(line);
      const body = [];
      i += 2;
      while (i < lines.length && isTableRow(lines[i])) { body.push(splitRow(lines[i])); i++; }
      i--;
      out.push('<div class="tscroll"><table>');
      out.push(`<thead><tr>${head.map((c) => `<th>${inlineMd(c)}</th>`).join("")}</tr></thead>`);
      out.push("<tbody>");
      for (const row of body) {
        out.push(`<tr>${row.map((c) => `<td>${cellHtml(c)}</td>`).join("")}</tr>`);
      }
      out.push("</tbody></table></div>");
      continue;
    }

    const heading = line.match(/^(#{1,4})\s+(.*)$/);
    if (heading) {
      flushAll();
      const level = heading[1].length;
      const text = inlineMd(heading[2]);
      // h2 carries the sample's numbered section-head treatment (the counter
      // lives in CSS, so the numbering stays correct however many sections the
      // model wrote).
      out.push(`<h${level}>${text}</h${level}>`);
      continue;
    }

    // Checked before the bullet rule: "---" also matches nothing else, but a
    // lone "-" run must not be read as an empty list item.
    if (/^\s*(---+|\*\*\*+|___+)\s*$/.test(line)) { flushAll(); out.push("<hr>"); continue; }

    if (/^\s*>\s?/.test(line)) {
      flushPara();
      closeList();
      quote.push(inlineMd(line.replace(/^\s*>\s?/, "")));
      continue;
    }

    const bullet = line.match(/^\s*[-*+]\s+(.*)$/);
    if (bullet) {
      flushPara(); flushQuote();
      openList("ul");
      out.push(`<li>${inlineMd(bullet[1])}</li>`);
      continue;
    }

    const ordered = line.match(/^\s*\d+[.)]\s+(.*)$/);
    if (ordered) {
      flushPara(); flushQuote();
      openList("ol");
      out.push(`<li>${inlineMd(ordered[1])}</li>`);
      continue;
    }

    if (line.trim() === "") { flushAll(); continue; }

    // Plain prose. A run of these lines is a single paragraph, so only the
    // list/quote runs are closed here — the paragraph buffer keeps filling.
    flushQuote();
    closeList();
    para.push(inlineMd(line.trim()));
  }
  flushAll();
  return out.join("\n");
}

/** The report stylesheet -- adapted from the W35 sample. */
export const REPORT_CSS = `
:root{
  --ground:#EEF1F0; --panel:#FFFFFF;
  --ink:#16201F; --ink2:#55635F; --ink3:#8A9794; --rule:#C9D2CF;
  --steel:#2C5F7C; --green:#1B7A50; --amber:#C98A0E; --amber-d:#A8630D;
  --red:#B3372B; --plum:#6B4E9E;
  --disp:'Archivo','Helvetica Neue',Arial,sans-serif;
  --body:'IBM Plex Sans',system-ui,-apple-system,sans-serif;
  --mono:'IBM Plex Mono',ui-monospace,'SFMono-Regular',Menlo,Consolas,monospace;
}
*{box-sizing:border-box}
html{-webkit-text-size-adjust:100%}
body{margin:0;background:var(--ground);color:var(--ink);font-family:var(--body);font-size:15px;line-height:1.55}
.wrap{max-width:1180px;margin:0 auto;padding:0 24px 72px;counter-reset:sec}

.mast{padding:36px 0 20px;border-bottom:2px solid var(--ink)}
.eyebrow{font-family:var(--mono);font-size:11px;font-weight:500;letter-spacing:.18em;text-transform:uppercase;color:var(--ink2);margin:0 0 12px}
.mast h1{font-family:var(--disp);font-weight:700;font-size:clamp(26px,4.6vw,46px);line-height:1;letter-spacing:-.02em;margin:0 0 6px;text-transform:uppercase}
.mast .sub{font-family:var(--disp);font-weight:500;font-size:clamp(14px,2.2vw,19px);color:var(--ink2);margin:0}
.meta{display:flex;flex-wrap:wrap;gap:0 34px;margin-top:20px;font-family:var(--mono);font-size:11.5px;color:var(--ink2)}
.meta b{display:block;color:var(--ink3);font-weight:500;letter-spacing:.14em;text-transform:uppercase;font-size:10px}
.meta span{color:var(--ink);font-weight:500}

.readout{display:grid;grid-template-columns:repeat(auto-fit,minmax(132px,1fr));gap:1px;background:var(--rule);border:1px solid var(--rule);margin:24px 0 0}
.cell{background:var(--panel);padding:14px 15px 13px}
.cell .k{font-family:var(--mono);font-size:9.5px;letter-spacing:.15em;text-transform:uppercase;color:var(--ink3);margin-bottom:7px}
.cell .v{font-family:var(--mono);font-size:27px;font-weight:600;line-height:1;letter-spacing:-.02em}
.cell .d{font-family:var(--mono);font-size:10.5px;color:var(--ink2);margin-top:5px}
.cell.alarm .v{color:var(--red)} .cell.good .v{color:var(--green)} .cell.warn .v{color:var(--amber-d)}

section{margin-top:44px}
.shead{display:flex;align-items:baseline;gap:14px;border-bottom:1px solid var(--ink);padding-bottom:9px;margin-bottom:18px}
.shead .no{font-family:var(--mono);font-size:11px;font-weight:600;color:var(--red);letter-spacing:.08em}
.shead h2{font-family:var(--disp);font-weight:600;text-transform:uppercase;font-size:clamp(15px,2.1vw,19px);letter-spacing:.02em;margin:0;border:0;padding:0;display:block}
.shead h2::before{content:none}
.shead .note{margin-left:auto;font-family:var(--mono);font-size:10.5px;color:var(--ink3);text-align:right}

/* Bare h2 (markdown-sourced) gets the same numbered section-head treatment. */
h1{font-family:var(--disp);font-weight:700;font-size:clamp(22px,3.4vw,32px);line-height:1.05;letter-spacing:-.02em;margin:34px 0 12px;text-transform:uppercase}
h2{counter-increment:sec;font-family:var(--disp);font-weight:600;text-transform:uppercase;font-size:clamp(15px,2.1vw,19px);letter-spacing:.02em;margin:42px 0 18px;padding-bottom:9px;border-bottom:1px solid var(--ink);display:flex;align-items:baseline;gap:14px}
h2::before{content:counter(sec,decimal-leading-zero);font-family:var(--mono);font-size:11px;font-weight:600;color:var(--red);letter-spacing:.08em;flex:none}
h3{font-family:var(--mono);font-size:10.5px;font-weight:600;letter-spacing:.13em;text-transform:uppercase;color:var(--ink);margin:26px 0 8px;padding-bottom:7px;border-bottom:1px dashed var(--rule)}
h4{font-family:var(--disp);font-weight:600;font-size:14px;margin:20px 0 6px}

p{margin:.6rem 0;max-width:86ch}
ul,ol{margin:.6rem 0;padding-left:1.15rem;max-width:86ch}
li{margin:.28rem 0;line-height:1.55}
li::marker{color:var(--ink3)}
strong{font-weight:600;color:var(--ink)}
code{font-family:var(--mono);font-size:.86em;background:#E4E9E7;padding:1px 5px}
hr{border:0;border-top:1px solid var(--rule);margin:26px 0}
blockquote{margin:14px 0;padding:12px 16px;background:var(--panel);border-left:3px solid var(--amber);font-size:14.5px;max-width:86ch}
blockquote p{margin:0}
a{color:var(--steel)}

.tscroll{overflow-x:auto;background:var(--panel);border:1px solid var(--rule);margin:16px 0}
table{width:100%;border-collapse:collapse;font-size:13px;min-width:640px}
th{font-family:var(--mono);font-size:9.5px;font-weight:600;letter-spacing:.13em;text-transform:uppercase;color:var(--ink2);text-align:left;padding:10px 12px;border-bottom:1px solid var(--ink);white-space:nowrap}
td{padding:9px 12px;border-bottom:1px solid var(--rule);vertical-align:top}
tbody tr:last-child td{border-bottom:none}
td.m{font-family:var(--mono);font-size:11.5px;white-space:nowrap}
td.grp{font-family:var(--mono);font-size:10px;letter-spacing:.09em;text-transform:uppercase;color:var(--ink2);white-space:nowrap}
td.num,th.num{font-family:var(--mono);font-size:12px;text-align:right;white-space:nowrap}
tr.bandhead td{background:#E4E9E7;font-family:var(--mono);font-size:9.5px;font-weight:600;letter-spacing:.14em;text-transform:uppercase;color:var(--ink);padding:8px 12px}
tr.bandhead td span{color:var(--ink3);font-weight:400;letter-spacing:.06em}
tr.sev td{background:#FBF0EE}

.pill{display:inline-block;font-family:var(--mono);font-size:9.5px;font-weight:600;letter-spacing:.1em;text-transform:uppercase;padding:2px 6px;color:#fff;white-space:nowrap}
.p-open{background:var(--ink3)} .p-wip{background:var(--steel)} .p-test{background:var(--amber)}
.p-uat{background:var(--amber-d)} .p-cut{background:var(--plum)} .p-live{background:var(--green)}
.p-hold{background:var(--red)} .p-new{background:var(--ink)} .p-hi{background:var(--red)}
.p-med{background:var(--ink3)} .p-low{background:#A8B3B0}
.od{color:var(--red);font-weight:600}

.assess{background:var(--panel);border:1px solid var(--rule);padding:6px 0}
.aline{display:grid;grid-template-columns:34px 1fr;gap:0 4px;padding:8px 18px;border-bottom:1px solid var(--rule)}
.aline:last-child{border-bottom:none}
.aline .n{font-family:var(--mono);font-size:11px;font-weight:600;color:var(--ink3)}
.aline .t{font-size:14.5px;line-height:1.45}

.legend{display:flex;flex-wrap:wrap;gap:5px 16px;margin:0 0 18px;font-family:var(--mono);font-size:10px;letter-spacing:.06em;text-transform:uppercase;color:var(--ink2)}
.legend i{display:inline-block;width:9px;height:9px;margin-right:5px;vertical-align:-1px}
.sw-open{background:var(--ink3)} .sw-wip{background:var(--steel)} .sw-test{background:var(--amber)}
.sw-uat{background:var(--amber-d)} .sw-cut{background:var(--plum)} .sw-live{background:var(--green)}

/* The key that buildReportDoc emits above the report body. The .legend class
   and its swatches already existed here but nothing ever emitted one, so every
   colour in a generated report was unexplained. sw-hold/good/warn/alarm are the
   swatches that were missing; the rest reuse the pill colours exactly, so the
   key cannot drift from what it describes.

   NO BACKTICKS ANYWHERE IN THIS STRING. REPORT_CSS is a template literal, so a
   backtick in a comment terminates it and turns the rest of the stylesheet into
   JavaScript. That is still valid syntax, so the build passes and the failure
   only appears at runtime — as a module-level throw on import, which blanks the
   entire app. */
.keybox{border:1px solid var(--rule);background:var(--panel);padding:11px 15px;margin:0 0 24px}
.keybox .kh{font-family:var(--mono);font-size:9.5px;font-weight:600;letter-spacing:.14em;text-transform:uppercase;color:var(--ink3);margin:0 0 8px}
.keybox .legend{margin:0}
.keybox .legend + .legend{margin-top:7px;padding-top:8px;border-top:1px solid var(--rule)}
.keybox .legend b{font-weight:600;color:var(--ink)}
.sw-hold{background:var(--red)} .sw-good{background:var(--green)}
.sw-warn{background:var(--amber-d)} .sw-alarm{background:var(--red)}
.sw-sev{background:#FBF0EE;border:1px solid var(--rule)}

.initgrp{margin-bottom:26px}
.initname{font-family:var(--mono);font-size:10.5px;font-weight:600;letter-spacing:.13em;text-transform:uppercase;color:var(--ink);padding:0 0 7px;border-bottom:1px dashed var(--rule);margin-bottom:2px}
.initname em{font-style:normal;color:var(--ink3);font-weight:400;letter-spacing:.06em}
.initname .seq{color:var(--red);margin-right:8px}
.prow{display:grid;grid-template-columns:minmax(190px,1.35fr) minmax(200px,1.6fr) 92px;gap:16px;align-items:center;padding:11px 0;border-bottom:1px solid var(--rule)}
.pname{font-weight:500;font-size:14px;line-height:1.3}
.pname .pm{display:block;font-family:var(--mono);font-size:10px;color:var(--ink3);letter-spacing:.05em;margin-top:3px;text-transform:uppercase}
.rail{display:flex;height:20px;background:#DFE5E3;overflow:hidden}
.rail .seg{display:flex;align-items:center;justify-content:center;font-family:var(--mono);font-size:10.5px;font-weight:600;color:#fff;min-width:20px}
.rail .empty{flex:1;display:flex;align-items:center;padding-left:9px;font-family:var(--mono);font-size:9.5px;letter-spacing:.14em;text-transform:uppercase;color:var(--ink3)}
.s-open{background:var(--ink3)} .s-wip{background:var(--steel)} .s-test{background:var(--amber)}
.s-uat{background:var(--amber-d)} .s-cut{background:var(--plum)} .s-live{background:var(--green)}
.pstat{font-family:var(--mono);font-size:10px;letter-spacing:.1em;text-transform:uppercase;color:var(--ink2);text-align:right}
.pstat .hi,.pstat .pri{color:var(--red);font-weight:600}

.supgrp{background:var(--panel);border:1px solid var(--rule);border-left:3px solid var(--ink3);padding:14px 18px}
.supgrp .sl{font-family:var(--mono);font-size:10px;letter-spacing:.14em;text-transform:uppercase;color:var(--ink2);margin-bottom:10px}
.supline{display:flex;flex-wrap:wrap;gap:4px 14px;align-items:baseline;padding:7px 0;border-bottom:1px dotted var(--rule);font-size:14px}
.supline:last-child{border-bottom:none}
.supline .nm{font-weight:500;min-width:210px}
.supline .fig{font-family:var(--mono);font-size:11px;color:var(--ink2)}

.flag{background:var(--panel);border-left:3px solid var(--amber);padding:15px 18px;margin:16px 0 0}
.flag.crit{border-left-color:var(--red)}
.flag .t{font-family:var(--mono);font-size:10px;letter-spacing:.15em;text-transform:uppercase;color:var(--ink2);margin-bottom:6px}
.flag p{margin:0;font-size:14.5px;max-width:82ch}

.ownblk{margin-bottom:22px}
.ownhead{display:flex;flex-wrap:wrap;align-items:baseline;gap:0 12px;border-bottom:1px solid var(--ink);padding-bottom:7px}
.ownhead .who{font-family:var(--disp);font-weight:600;font-size:15px;text-transform:uppercase;letter-spacing:.03em}
.ownhead .role{font-family:var(--mono);font-size:10px;letter-spacing:.12em;text-transform:uppercase;color:var(--ink3)}
.ownhead .cnt{margin-left:auto;font-family:var(--mono);font-size:11px;color:var(--red);font-weight:600}

footer{margin-top:44px;padding-top:16px;border-top:2px solid var(--ink);font-family:var(--mono);font-size:10.5px;color:var(--ink3);line-height:1.7}
footer a{color:var(--steel)}

@media (max-width:720px){
  .prow{grid-template-columns:1fr;gap:8px}
  .pstat{text-align:left}
  .meta{gap:0 22px}
  .supline .nm{min-width:0;width:100%}
}
@media print{
  body{background:#fff}
  .wrap{max-width:none;padding:0}
  section{break-inside:avoid;margin-top:26px}
  h2{break-after:avoid}
  .tscroll{overflow:visible;border:1px solid var(--rule)}
  table{min-width:0;font-size:10.5px}
}
`;

const FONT_LINKS = [
  '<link rel="preconnect" href="https://fonts.googleapis.com">',
  '<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>',
  '<link href="https://fonts.googleapis.com/css2?family=Archivo:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500;600&family=IBM+Plex+Sans:wght@400;500;600&display=swap" rel="stylesheet">',
].join("\n");

/**
 * Wraps report body HTML in the full standalone document -- the same string is
 * used for the iframe preview and the download, so what you see is what you get.
 */
/*
  The colour key, above the report body.

  Every generated report and digest uses colour to carry meaning — status
  pills, a KPI figure turning amber or red, a tinted severity row, red text for
  an overdue date — and none of it was ever explained. A reader who has not
  seen the palette before cannot tell whether amber means "in test" or "at
  risk" (it means both, in different places, which is exactly why this is
  needed).

  IT DESCRIBES ONLY WHAT THIS REPORT ACTUALLY CONTAINS. The classes are matched
  against the body, so a member's progress summary with no cutover pill does
  not carry a legend entry for one, and a report with no severity rows does not
  explain a tint it never uses. A key listing colours that are not on the page
  is just more to read.

  The swatches reuse the pill and value colours from REPORT_CSS by class rather
  than restating hex values, so the key cannot drift from the thing it is
  describing.

  NOTE: reports whose stored content is a FULL HTML document bypass this
  builder entirely (see isFullHtmlDoc in the pages that call it), so they get
  no key. Everything the current generators produce is a fragment.
*/
function buildKey(bodyHtml) {
  const body = String(bodyHtml || "");

  /*
    Matched INSIDE a class attribute, not anywhere in the document. A bare
    /\bwarn\b/ would also fire on the word "warn" in the report's own prose,
    and /\bod\b/ on any stray "od" — producing a key entry for a colour that is
    nowhere on the page. Both quote styles, since this HTML is model-written.
  */
  const has = (cls) => new RegExp(`class=["'][^"']*\\b${cls}\\b`).test(body);

  // Workflow states, in pipeline order — the same order and colours as the
  // task_status enum's pills.
  const stages = [
    ["p-open", "sw-open", "Open"],
    ["p-wip", "sw-wip", "In progress"],
    ["p-test", "sw-test", "Test"],
    ["p-uat", "sw-uat", "UAT"],
    ["p-cut", "sw-cut", "Cutover"],
    ["p-live", "sw-live", "Live"],
    ["p-hold", "sw-hold", "On hold"],
  ].filter(([cls]) => has(cls));

  // How to read a coloured number or row.
  const signals = [
    [() => has("cell good"), "sw-good", "Healthy"],
    [() => has("cell warn"), "sw-warn", "Needs attention"],
    [() => has("cell alarm"), "sw-alarm", "At risk"],
    [() => has("od"), "sw-alarm", "Past"],
    [() => has("sev"), "sw-sev", "Flagged row"],
  ].filter(([test]) => test());

  if (stages.length === 0 && signals.length === 0) return "";

  const row = (items) =>
    `<div class="legend">${items
      .map(([, sw, label]) => `<span><i class="${sw}"></i>${esc(label)}</span>`)
      .join("")}</div>`;

  return `<div class="keybox">
  <p class="kh">How to read this report</p>
  ${stages.length ? row(stages.map(([, sw, label]) => [null, sw, label])) : ""}
  ${signals.length ? row(signals.map(([, sw, label]) => [null, sw, label])) : ""}
</div>`;
}

export function buildReportDoc({ title, eyebrow, subtitle, meta = [], bodyHtml, footer }) {
  const metaHtml = meta.length
    ? `<div class="meta">${meta
        .map((m) => `<div><b>${esc(m.label)}</b><span>${esc(m.value)}</span></div>`)
        .join("")}</div>`
    : "";

  const keyHtml = buildKey(bodyHtml);

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>${esc(title)}</title>
${FONT_LINKS}
<style>${REPORT_CSS}</style>
</head>
<body>
<div class="wrap">
<header class="mast">
  <p class="eyebrow">${esc(eyebrow || "NG-IT Initiative Board")}</p>
  <h1>${esc(title)}</h1>
  ${subtitle ? `<p class="sub">${esc(subtitle)}</p>` : ""}
  ${metaHtml}
</header>
${keyHtml}
${bodyHtml}
${footer ? `<footer>${footer}</footer>` : ""}
</div>
</body>
</html>`;
}

/*
  The report shell caps the page at 1180px and prose at ~86 characters, which
  is right for the side-by-side preview but made the Expand dialog look
  identical: a much wider window around the same narrow column. For the
  expanded view only, lift those caps so the document uses the whole width.
  Applied to a copy — the stored/downloaded/emailed HTML is untouched.
*/
const EXPANDED_CSS =
  ".wrap{max-width:none!important;padding:0 40px 72px!important}" +
  "p,ul,ol,blockquote,.flag p{max-width:none!important}" +
  "table{min-width:0!important}";

export function widenReportDoc(html) {
  if (!html) return html;
  const tag = `<style>${EXPANDED_CSS}</style>`;
  return html.includes("</head>") ? html.replace("</head>", `${tag}</head>`) : tag + html;
}

/** Filesystem-safe filename for the download button. */
export function reportFileName(label, date) {
  const slug =
    String(label || "report")
      .replace(/[^A-Za-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 60) || "report";
  const d = date instanceof Date && !Number.isNaN(date.getTime()) ? date : new Date();
  return `NG-IT-Report_${slug}_${d.toISOString().slice(0, 10)}.html`;
}
